import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { clone, isEmpty, isObject, isString } from 'lodash-es'
import { cloneResource } from '#src/utilsExtendedResource.ts'

type ValueOrArrayElement<V> = V extends (infer U)[] ? V | U : V

export interface PatchOperation<T extends ResourceType> {
  readonly op: 'add' | 'remove' | 'replace' | 'copy' | 'move' | 'test'
  readonly path: string
  readonly value?:
    | ValueOrArrayElement<ExtractResource<T>[keyof ExtractResource<T>]>
    | string
    | number
    | boolean
    | null
    | undefined
}

type DisallowedArrayMethods =
  | 'pop'
  | 'shift'
  | 'unshift'
  | 'splice'
  | 'reverse'
  | 'sort'
  | 'fill'
  | 'copyWithin'

export type PushOnlyArray<T> = Omit<T[], DisallowedArrayMethods>

export type DeepPushOnlyArray<T> = T extends (infer U)[]
  ? PushOnlyArray<DeepPushOnlyArray<U>>
  : T extends object
    ? { [K in keyof T]: DeepPushOnlyArray<T[K]> }
    : T

const DISALLOWED_ARRAY_METHODS: DisallowedArrayMethods[] = [
  'pop',
  'shift',
  'unshift',
  'splice',
  'reverse',
  'sort',
  'fill',
  'copyWithin',
]

function isKeyOf<O extends object>(
  object: O,
  key: string | symbol
): key is Extract<keyof O, string | symbol> {
  return key in object
}

export class MonitoredResource<T extends ResourceType> {
  private _patches: PatchOperation<T>[] = []
  readonly proxy: DeepPushOnlyArray<ExtractResource<T>>

  constructor(resource: ExtractResource<T>) {
    this.proxy = this.createProxy(resource, '')
  }

  get patches(): PatchOperation<T>[] {
    return [...this._patches]
  }

  private addPatch(patch: PatchOperation<T>): void {
    if (patch.op === 'replace') {
      const existingIndex = this._patches.findIndex(
        (p) => p.op === 'replace' && p.path === patch.path
      )
      if (existingIndex !== -1) {
        this._patches.splice(existingIndex, 1)
      }
    }
    this._patches.push(patch)
  }

  private createArrayProxy<U>(
    array: U[],
    path: string,
    isNewArray?: boolean,
    onArrayInitialization?: (target: U[]) => void
  ): PushOnlyArray<U> {
    const handler: ProxyHandler<U[]> = {
      get: (target, prop, receiver) => {
        if (prop === 'push') {
          return (...items: U[]) => {
            const result = target.push(...items)
            if (isNewArray && items.length > 0) {
              // If this is a new array and we're pushing items, create a single patch
              onArrayInitialization?.(target)
              this.addPatch({
                op: 'add',
                path,
                value: items as any,
              })
            } else {
              // Otherwise, add items as usual
              for (const item of items) {
                this.addPatch({
                  op: 'add',
                  path: `${path}/-`,
                  value: item as ValueOrArrayElement<
                    ExtractResource<T>[keyof ExtractResource<T>]
                  >,
                })
              }
            }
            return result
          }
        }

        if (prop === 'length') {
          return target.length
        }
        if (prop === Symbol.iterator) {
          return target[Symbol.iterator]
        }

        if (typeof prop === 'string' && !Number.isNaN(Number(prop))) {
          const index = Number(prop)
          const value = isKeyOf(target, prop) ? target[prop] : undefined
          if (typeof value === 'object' && value !== null) {
            return this.createProxy(value, `${path}/${index}`)
          }
          return value
        }

        if (DISALLOWED_ARRAY_METHODS.includes(prop as DisallowedArrayMethods)) {
          throw new Error(
            `Operation ${String(prop)} is not allowed on this array.`
          )
        }
        return isKeyOf(target, prop) ? target[prop] : undefined
      },

      set: (target, prop, value, receiver) => {
        if (typeof prop === 'string' && !Number.isNaN(Number(prop))) {
          const index = Number(prop)
          this.addPatch({
            op: index >= target.length ? 'add' : 'replace',
            path: `${path}/${index}`,
            value,
          })
          return Reflect.set(target, prop, value, receiver)
        }

        throw new Error(`Setting values directly is not allowed on this array.`)
      },
    }

    return new Proxy(array, handler) as PushOnlyArray<U>
  }

  private createProxy(
    obj: any,
    path: string
  ): DeepPushOnlyArray<ExtractResource<T>> {
    const handler: ProxyHandler<any> = {
      get: (target, key, receiver) => {
        const value = target[key]
        const newPath = `${path}/${String(key)}`

        if (value === undefined) {
          if (Array.isArray(target)) {
            throw new Error(
              `Index ${String(key)} is out of bounds for array at path ${path}`
            )
          }
          return value
        }

        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            return this.createArrayProxy(value, newPath)
          }

          return this.createProxy(value, newPath)
        }

        return value
      },

      set: (target, key, value, receiver) => {
        const currentValue = target[key]
        const newPath = `${path}/${String(key)}`

        if (currentValue === undefined) {
          if (value === undefined) {
            // No patch needed when setting undefined to undefined
            return Reflect.set(target, key, value, receiver)
          }
          const shouldClone =
            (isObject(value) || Array.isArray(value)) && isEmpty(value)
          this.addPatch({
            op: 'add',
            path: newPath,
            value: shouldClone ? clone(value) : value,
          })
        } else if (currentValue !== value) {
          if (
            value === undefined ||
            (Array.isArray(value) && isEmpty(value)) ||
            (isString(currentValue) && !value)
          ) {
            this.addPatch({
              op: 'remove',
              path: newPath,
            })
          } else {
            this.addPatch({
              op: 'replace',
              path: newPath,
              value,
            })
          }
        }

        return Reflect.set(target, key, value, receiver)
      },

      deleteProperty: (target, key) => {
        // Only emit a remove patch if the property actually exists.
        // A remove op on a non-existent path causes FHIR servers to reject
        // the patch (e.g. Condition.clinicalStatus after entered-in-error).
        if (Object.hasOwn(target, key)) {
          const newPath = `${path}/${String(key)}`
          this.addPatch({
            op: 'remove',
            path: newPath,
          })
        }
        return Reflect.deleteProperty(target, key)
      },
    }

    return new Proxy(obj, handler) as DeepPushOnlyArray<ExtractResource<T>>
  }
}

export function monitorResource<T extends ResourceType>(
  resource: ExtractResource<T>
): MonitoredResource<T> {
  return new MonitoredResource(
    // deep copy the resource to avoid modifying the original during failed patches
    cloneResource(resource)
  )
}
